import cn from "@/utils/cn"
import { useCallback, useEffect, useRef } from "react"

type CSFlagButtonProps = {
  isFlagged: boolean
  csFlagNotes?: string
  onClick: () => void
  isPending?: boolean
  showTooltip?: boolean
}

const CSFlagButton = ({
  isFlagged,
  csFlagNotes,
  onClick,
  isPending = false,
  showTooltip = true,
}: CSFlagButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    if (!isPending) {
      onClick()
    }
  }

  const updateTooltipPosition = useCallback(() => {
    if (buttonRef.current && tooltipRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      tooltipRef.current.style.top = `${rect.bottom + 8}px`
      tooltipRef.current.style.left = `${rect.left + rect.width / 2}px`
    }
  }, [])

  // React does not re-render the component when the tooltip is shown,
  // so we need to update the position manually.
  useEffect(() => {
    if (showTooltip || isFlagged) {
      window.addEventListener("scroll", updateTooltipPosition, true)
      window.addEventListener("resize", updateTooltipPosition)

      return () => {
        window.removeEventListener("scroll", updateTooltipPosition, true)
        window.removeEventListener("resize", updateTooltipPosition)
      }
    }
  }, [showTooltip, isFlagged, updateTooltipPosition])

  return (
    <div className="group relative inline-block">
      <button
        ref={buttonRef}
        onClick={handleClick}
        disabled={isPending}
        className={cn(
          "flex items-center justify-center transition-colors",
          isPending ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        )}
        aria-label={isFlagged ? "Edit CS flag" : "Add CS flag"}
      >
        <span
          className={cn(
            "material-icons text-[20px]",
            isFlagged ? "text-red-600" : "text-gray-400"
          )}
        >
          {isFlagged ? "flag" : "outlined_flag"}
        </span>
      </button>
      {showTooltip && isFlagged && (
        <div
          ref={tooltipRef}
          className={cn(
            "fixed z-[9999] hidden w-64 -translate-x-1/2 rounded bg-primary px-3 py-2 text-xs text-white",
            "group-hover:block"
          )}
        >
          {csFlagNotes || "No notes added."}
          <br />
          <span className="mt-1 block italic">Click to edit</span>
        </div>
      )}
    </div>
  )
}

export default CSFlagButton
